import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import {
  Loader2, ArrowLeft, RefreshCw, Mail, Eye, MousePointerClick,
  AlertTriangle, CheckCircle2, Users, Filter,
} from 'lucide-react';

const SEQUENCE_COLOURS = {
  onboarding: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  activation: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  engagement: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  trial:      'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

const fmtPct = (n) => `${Math.round((n || 0) * 100)}%`;
const fmtDelay = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h} h`;
  return `${Math.round(h / 24)} d`;
};
const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const Stat = ({ label, value, hint, accent }) => (
  <div className={`rounded-md border p-5 ${accent ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'}`}
    data-testid={`lifecycle-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`mt-1 text-3xl font-mono font-bold ${accent ? 'text-primary' : 'text-foreground'}`}>{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const SequenceBadge = ({ sequenceId }) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${SEQUENCE_COLOURS[sequenceId] || 'bg-muted text-muted-foreground border-border'}`}>
    {sequenceId}
  </span>
);

const FunnelBar = ({ label, count, max }) => {
  const pct = max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 4;
  return (
    <div className="space-y-1" data-testid={`funnel-bar-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-semibold text-foreground">{count}</span>
      </div>
      <div className="h-2 rounded bg-muted/30 overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const AdminLifecyclePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(false);
  const [stepFilter, setStepFilter] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statsRes, sendsRes] = await Promise.all([
          adminApi.lifecycleStats(),
          adminApi.lifecycleSends({ limit: 100, step_id: stepFilter || undefined }),
        ]);
        if (cancelled) return;
        setStats(statsRes.data);
        setSends(sendsRes.data || []);
      } catch (e) {
        if (e.response?.status === 401) {
          clearAdminToken();
          navigate('/admin/login', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, stepFilter, reloadKey]);

  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleRefresh = () => { setRefreshing(true); triggerReload(); };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await adminApi.lifecycleRunNow();
      const total = res?.data?.total ?? 0;
      alert(`Sweep complete — ${total} email${total === 1 ? '' : 's'} sent.`);
      triggerReload();
    } catch {
      alert('Sweep failed — check backend logs.');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const funnelMax = Math.max(1, ...(stats?.funnel || []).map((f) => f.tenants));
  const totalSent = (stats?.per_step || []).reduce((a, b) => a + b.sent, 0);
  const totalOpened = (stats?.per_step || []).reduce((a, b) => a + b.opened, 0);
  const totalClicked = (stats?.per_step || []).reduce((a, b) => a + b.clicked, 0);
  const overallOpenRate = totalSent ? totalOpened / totalSent : 0;
  const overallClickRate = totalSent ? totalClicked / totalSent : 0;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-lifecycle-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/tenants')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="lifecycle-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Lifecycle Emails</h1>
              <p className="text-sm text-muted-foreground">
                Per-step performance + activation funnel across every tenant.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-border rounded hover:bg-muted/30 disabled:opacity-40"
              data-testid="lifecycle-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleRunNow}
              disabled={running}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-primary text-black rounded font-semibold hover:bg-primary/90 disabled:opacity-40"
              data-testid="lifecycle-run-now-btn"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              Run sweep now
            </button>
          </div>
        </div>

        {/* Topline stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Tenants" value={stats?.total_tenants ?? 0} hint="Active workspaces" />
          <Stat label="Emails Sent" value={stats?.total_sends ?? 0} hint="Lifetime delivered" accent />
          <Stat label="Open Rate" value={fmtPct(overallOpenRate)} hint={`${totalOpened} of ${totalSent}`} />
          <Stat label="Click Rate" value={fmtPct(overallClickRate)} hint={`${totalClicked} of ${totalSent}`} />
        </div>

        {/* Activation funnel */}
        <div className="rounded-md border border-border bg-card p-6 mb-8" data-testid="lifecycle-funnel">
          <div className="flex items-center gap-2 mb-5">
            <Users className="w-4 h-4 text-primary" />
            <h2 className="text-sm uppercase tracking-widest font-semibold">Activation funnel</h2>
            <span className="text-xs text-muted-foreground ml-2">distinct tenants per milestone</span>
          </div>
          <div className="space-y-4">
            {(stats?.funnel || []).map((row) => (
              <FunnelBar key={row.event_type} label={row.label} count={row.tenants} max={funnelMax} />
            ))}
          </div>
        </div>

        {/* Per-step table */}
        <div className="rounded-md border border-border bg-card mb-8 overflow-hidden" data-testid="lifecycle-per-step-table">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            <h2 className="text-sm uppercase tracking-widest font-semibold">Per-step performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/20 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-6 py-3 font-medium">Step</th>
                  <th className="text-left px-3 py-3 font-medium">Trigger · delay</th>
                  <th className="text-right px-3 py-3 font-medium">Sent</th>
                  <th className="text-right px-3 py-3 font-medium">Opened</th>
                  <th className="text-right px-3 py-3 font-medium">Clicked</th>
                  <th className="text-right px-3 py-3 font-medium">Bounced</th>
                  <th className="text-right px-3 py-3 font-medium">Open %</th>
                  <th className="text-right px-3 py-3 font-medium">Click %</th>
                  <th className="text-right px-6 py-3 font-medium">Filter</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(stats?.per_step || []).map((row) => {
                  const active = stepFilter === row.step_id;
                  return (
                    <tr
                      key={row.step_id}
                      className={active ? 'bg-primary/5' : ''}
                      data-testid={`lifecycle-step-row-${row.step_id}`}
                    >
                      <td className="px-6 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <SequenceBadge sequenceId={row.sequence_id} />
                          <span className="font-mono text-xs text-foreground">{row.step_id.split('.').slice(1, 2).join('.')}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top text-xs text-muted-foreground">
                        <div>{row.trigger}</div>
                        <div className="font-mono">{fmtDelay(row.delay_hours)}</div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono">{row.sent}</td>
                      <td className="px-3 py-3 text-right font-mono">{row.opened}</td>
                      <td className="px-3 py-3 text-right font-mono">{row.clicked}</td>
                      <td className="px-3 py-3 text-right font-mono">
                        <span className={row.bounced ? 'text-red-400' : ''}>{row.bounced}</span>
                      </td>
                      <td className="px-3 py-3 text-right font-mono">{fmtPct(row.open_rate)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtPct(row.click_rate)}</td>
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => setStepFilter(active ? '' : row.step_id)}
                          className={`text-xs px-2 py-1 border rounded ${active ? 'border-primary text-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                          data-testid={`lifecycle-step-filter-${row.step_id}`}
                        >
                          {active ? 'Clear' : <Filter className="w-3 h-3 inline" />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {(stats?.per_step || []).length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-muted-foreground">
                      No lifecycle sends recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent sends */}
        <div className="rounded-md border border-border bg-card overflow-hidden" data-testid="lifecycle-recent-sends">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            <h2 className="text-sm uppercase tracking-widest font-semibold">Recent sends</h2>
            {stepFilter && (
              <span className="text-xs text-muted-foreground ml-2 font-mono">
                filtered → {stepFilter}
              </span>
            )}
            <span className="ml-auto text-xs text-muted-foreground">{sends.length} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/20 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-6 py-3 font-medium">Sent</th>
                  <th className="text-left px-3 py-3 font-medium">Tenant</th>
                  <th className="text-left px-3 py-3 font-medium">Recipient</th>
                  <th className="text-left px-3 py-3 font-medium">Step</th>
                  <th className="text-left px-3 py-3 font-medium">Subject</th>
                  <th className="text-right px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sends.map((s, idx) => (
                  <tr key={`${s.tenant_id}-${s.step_id}-${idx}`} data-testid="lifecycle-recent-send-row">
                    <td className="px-6 py-3 align-top text-xs text-muted-foreground font-mono">{fmtTime(s.sent_at || s.queued_at)}</td>
                    <td className="px-3 py-3 align-top text-xs">{s.tenant_name}</td>
                    <td className="px-3 py-3 align-top text-xs font-mono">{s.recipient}</td>
                    <td className="px-3 py-3 align-top text-xs font-mono text-muted-foreground">
                      {(s.step_id || '').split('.').slice(0, 2).join('.')}
                    </td>
                    <td className="px-3 py-3 align-top text-xs">{s.subject || '—'}</td>
                    <td className="px-6 py-3 align-top text-right">
                      <div className="inline-flex items-center gap-2 text-xs">
                        {s.sent ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                        )}
                        {s.opened_at && (
                          <span title={`Opened ${fmtTime(s.opened_at)}`} className="text-blue-300">
                            <Eye className="w-3.5 h-3.5 inline" />
                          </span>
                        )}
                        {s.clicked_at && (
                          <span title={`Clicked ${fmtTime(s.clicked_at)}`} className="text-emerald-300">
                            <MousePointerClick className="w-3.5 h-3.5 inline" />
                          </span>
                        )}
                        {s.bounced_at && (
                          <span title="Bounced" className="text-red-400 text-[10px] uppercase">bounce</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {sends.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-muted-foreground text-sm">
                      No sends to show.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLifecyclePage;
